import React, { useState } from 'react';
import { Download, Upload, AlertTriangle, CheckCircle2, FileCode, ScrollText } from 'lucide-react';
import { exportData, importData } from '../../services/backupService';
import { cn } from '../../lib/utils';

export const BackupRestore = () => {
  const isAiStudio = window.location.hostname.includes("run.app") || window.location.hostname.includes("localhost");
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingZip, setIsExportingZip] = useState(false);
  const [isExportingRules, setIsExportingRules] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const handleExportRules = async () => {
    setIsExportingRules(true);
    try {
      const rules = `
# RÈGLES D'OR DU PROJET CÉVEN'VTC (LE MARBRE)

## 1. ARCHITECTURE & DONNÉES
- Architecture "File-Based" : Manipulation d'objets JSON structurés (appData).
- Exportabilité Totale : L'administrateur possède physiquement ses données (JSON/ZIP).
- Abstraction : Appels Firebase isolés dans storageService.ts.
- Médias : URLs persistantes, pas de verrouillage propriétaire.
- Persistance : Sauvegarde automatique des calculs complexes (Matrice, etc.).

## 2. UTILISATION DES API & SERVICES TIERS
- Indépendance & Gratuité : Priorité absolue à l'Open Source et au gratuit.
- Exploitation Interne : Toujours chercher à déduire ou collecter manuellement avant d'utiliser une API.
- Interdiction d'initiative : Ne jamais intégrer d'API externe sans preuve d'indispensabilité et accord explicite du propriétaire.

## 3. PERFORMANCE & ERGONOMIE
- Langue : Communication exclusivement en FRANÇAIS.
- Fluidité : Zéro latence, optimisation constante.
- Responsive : Optimisation obligatoire pour Desktop, Tablette et Smartphone.
- Contraste : Texte noir sur fond blanc (Admin), Texte blanc sur fond noir (Tunnel).
- Validation : Toujours demander les préférences du propriétaire avant d'agir.

## 4. LOGIQUE MÉTIER (CIRCUITS TOURISTIQUES)
- Baignade : Strictement interdite le matin (uniquement après 14h).
- Itinéraire : Calcul automatique de l'itinéraire le plus efficace (OSRM).
- Multi-jours : Répartition intelligente si > 10h.
- Jour 1 : Départ/Retour lieu de vacances client.
- Jours 2+ : Départ/Retour lieu d'hébergement ou domicile chauffeur.
- Restauration : Classement par fourchettes (Vert/Jaune/Orange/Rouge) selon préférences.
- Pique-nique : Insertion d'une pause dédiée (durée Admin).
`;
      const blob = new Blob([rules], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `instructions_systeme_cevenvtc_${new Date().toISOString().split('T')[0]}.txt`;
      a.click();
      setMessage({ type: 'success', text: 'Instructions système (Le Marbre) téléchargées !' });
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'exportation des instructions.' });
    } finally {
      setIsExportingRules(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const data = await exportData();
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_cerveau_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      setMessage({ type: 'success', text: 'Cerveau du site sauvegardé avec succès !' });
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'exportation.' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportZip = async () => {
    setIsExportingZip(true);
    try {
      const response = await fetch('/api/admin/backup-code');
      if (!response.ok) throw new Error('Erreur lors de la récupération du code');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `code_source_cevenvtc_${new Date().toISOString().split('T')[0]}.zip`;
      a.click();
      
      setMessage({ type: 'success', text: 'Code source (ZIP) téléchargé avec succès !' });
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'Erreur lors du téléchargement du code source.' });
    } finally {
      setIsExportingZip(false);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    if (!confirm('Attention : Cette action va écraser toutes les données actuelles. Êtes-vous sûr ?')) return;

    setIsImporting(true);
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const data = event.target?.result as string;
        await importData(data);
        setMessage({ type: 'success', text: 'Données restaurées avec succès !' });
      } catch (e) {
        setMessage({ type: 'error', text: 'Erreur lors de la restauration.' });
      } finally {
        setIsImporting(false);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-3xl border border-zinc-200 shadow-sm space-y-8">
      <h3 className="text-2xl font-bold flex items-center gap-2 text-zinc-900">
        <AlertTriangle className="w-6 h-6 text-amber-500" />
        Centre de Sauvegarde Totale
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-4">
          <h4 className="font-bold text-zinc-700">1. Archive Complète (ZIP)</h4>
          <p className="text-sm text-zinc-500">Contient les données, les métadonnées et les instructions de restauration.</p>
          <button 
            onClick={handleExportZip}
            disabled={isExportingZip || !isAiStudio}
            className={cn(
              "w-full flex items-center justify-center gap-2 p-4 bg-zinc-800 text-white rounded-xl font-bold transition-all disabled:opacity-50",
              !isAiStudio ? "cursor-not-allowed grayscale" : "hover:bg-zinc-900"
            )}
          >
            <FileCode className="w-5 h-5" />
            {isExportingZip ? 'Génération...' : 'Télécharger ZIP'}
          </button>
        </div>

        <div className="space-y-4">
          <h4 className="font-bold text-zinc-700">2. Plan de Maître & Données (JSON)</h4>
          <p className="text-sm text-zinc-500">Fichier JSON contenant 100% des données ET le plan détaillé de l'application (Blueprint).</p>
          <button 
            onClick={handleExport}
            disabled={isExporting}
            className="w-full flex items-center justify-center gap-2 p-4 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all disabled:opacity-50"
          >
            <Download className="w-5 h-5" />
            {isExporting ? 'Génération...' : 'Sauvegarder JSON (Master)'}
          </button>
        </div>

        <div className="space-y-4">
          <h4 className="font-bold text-zinc-700">3. Instructions Système (Le Marbre)</h4>
          <p className="text-sm text-zinc-500">Télécharge le fichier de règles pour l'IA (à coller dans System Instructions).</p>
          <button 
            onClick={handleExportRules}
            disabled={isExportingRules}
            className="w-full flex items-center justify-center gap-2 p-4 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 transition-all disabled:opacity-50"
          >
            <ScrollText className="w-5 h-5" />
            {isExportingRules ? 'Génération...' : 'Télécharger le Marbre'}
          </button>
        </div>

        <div className="space-y-4">
          <h4 className="font-bold text-zinc-700">4. Restauration</h4>
          <p className="text-sm text-zinc-500">Réinjecte une sauvegarde JSON dans le site.</p>
          <label className="flex items-center justify-center gap-2 p-4 bg-zinc-100 text-zinc-700 rounded-xl font-bold hover:bg-zinc-200 transition-all cursor-pointer">
            <Upload className="w-5 h-5" />
            {isImporting ? 'Import...' : 'Restaurer les données'}
            <input type="file" accept=".json" onChange={handleImport} className="hidden" />
          </label>
        </div>
      </div>

      <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-100 text-sm text-zinc-600">
        <p className="font-bold mb-2">Garantie d'exportabilité totale (Le Plan de Maître) :</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Le bouton <strong>Sauvegarder JSON (Master)</strong> est désormais votre "Plan de Maître". Il inclut 100% de vos données ET une spécification détaillée de toute la structure et la logique de l'application (Admin, Tunnel, Home).</li>
          <li>Le bouton <strong>Télécharger ZIP</strong> génère une archive contenant 100% du code source du site.</li>
          <li>Le bouton <strong>Le Marbre</strong> contient les règles de conduite pour l'IA.</li>
          <li>En combinant ces trois éléments, n'importe quel développeur peut reconstruire votre application à l'identique, sans même l'avoir vue auparavant.</li>
        </ul>
      </div>

      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          {message.text}
        </div>
      )}
    </div>
  );
};
